import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { Subject, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { PackageService } from '../../../../../services/package.service';
import { RewardsService } from '../../../../../services/rewards.service';
import {
  MultiSelectComponent,
  MultiSelectOption,
} from '../../../../../shared/components/wi-multi-select/wi-multi-select.component';
import { Package } from '../../../../../shared/types';
import {
  CreateRewardRuleRequest,
  RewardRule,
  RewardRuleServiceType,
} from '../../../../../rewards/models/rewards-v2.models';
import { ThemeColorPickerComponent } from '../../../../../shared/components/theme-color-picker';
import { DEFAULT_THEME_BACKGROUND } from '../../../../../shared/utils/theme-color.util';

const AMOUNT_MIN = 0.01;
const AMOUNT_MAX = 200;
const ITEMS_SOLD_MIN = 1;
const ITEMS_SOLD_MAX = 500;

function minArrayLength(min: number) {
  return (control: AbstractControl) => {
    const value = control.value;
    if (!Array.isArray(value) || value.length < min) {
      return { minArrayLength: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-reward-rule-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatRadioModule,
    MultiSelectComponent,
    ThemeColorPickerComponent,
  ],
  templateUrl: './reward-rule-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./reward-rule-modal.component.scss'],
})
export class RewardRuleModalComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() isOpen = false;
  @Input() editingRule: RewardRule | null = null;
  @Output() closeModal = new EventEmitter<void>();
  @Output() ruleCreated = new EventEmitter<void>();
  @Output() ruleUpdated = new EventEmitter<void>();

  form!: FormGroup;
  isSubmitting = false;
  isClosing = false;
  conflictError = '';
  isLoadingPackages = false;

  readonly itemsSoldOptions = Array.from({ length: ITEMS_SOLD_MAX }, (_, i) => i + 1);
  readonly serviceTypeOptions: { value: RewardRuleServiceType; label: string }[] = [
    { value: 'single_wash', label: 'Single Wash' },
    { value: 'membership', label: 'Membership' },
    // { value: 'prepaid', label: 'Prepaid' }, // Will unhide as part of support for prepaid-package rewards
  ];

  private allCarWashPackages: Package[] = [];
  packageOptions: MultiSelectOption[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly packageService: PackageService,
    private readonly rewardsService: RewardsService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.form
      .get('service_type')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((serviceType: RewardRuleServiceType) => {
        this.updatePackageOptions(serviceType);
      });
  }

  get isEditMode(): boolean {
    return this.editingRule != null;
  }

  get submitButtonLabel(): string {
    if (this.isSubmitting && !this.isClosing) {
      return this.isEditMode ? 'Updating...' : 'Creating...';
    }
    return this.isEditMode ? 'Update' : 'Create';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.resetModalState();
      if (this.editingRule) {
        this.populateFormFieldsFromRule(this.editingRule);
      }
      this.loadPackages();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose(): void {
    if (this.isSubmitting || this.isClosing) {
      return;
    }
    this.startClose();
  }

  onCancel(): void {
    this.onClose();
  }

  private startClose(): void {
    if (this.isClosing) {
      return;
    }
    this.isClosing = true;
    setTimeout(() => {
      this.closeModal.emit();
      this.isClosing = false;
      this.isSubmitting = false;
    }, 300);
  }

  onSubmit(): void {
    if (this.isSubmitting || this.isClosing) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.conflictError = '';

    const payload = this.buildPayload();
    const request$ = this.isEditMode
      ? this.rewardsService.updateRewardRule(this.editingRule!.id, payload)
      : this.rewardsService.createRewardRule(payload);

    request$.subscribe({
      next: () => {
        if (this.isEditMode) {
          this.ruleUpdated.emit();
        } else {
          this.ruleCreated.emit();
        }
        this.startClose();
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        this.conflictError = this.extractErrorMessage(error);
      },
    });
  }

  onPackageSelectionChange(selectedIds: string[]): void {
    this.form.get('selected_package_ids')?.setValue(selectedIds);
    this.form.get('selected_package_ids')?.markAsTouched();
    this.conflictError = '';
  }

  getFieldError(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (!field?.errors || !field.touched) {
      return '';
    }

    if (field.errors['required']) {
      return `${this.getFieldLabel(fieldName)} is required`;
    }
    if (field.errors['maxlength']) {
      return 'Reward name cannot exceed 100 characters';
    }
    if (field.errors['min'] || field.errors['max']) {
      if (fieldName === 'amount') {
        return `Reward amount must be between $${AMOUNT_MIN} and $${AMOUNT_MAX}`;
      }
      if (fieldName === 'items_sold') {
        return `Items sold must be between ${ITEMS_SOLD_MIN} and ${ITEMS_SOLD_MAX}`;
      }
    }
    if (field.errors['minArrayLength']) {
      return 'At least one wash type is required';
    }

    return 'Invalid value';
  }

  private buildForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      amount: ['', [Validators.required, Validators.min(AMOUNT_MIN), Validators.max(AMOUNT_MAX)]],
      service_type: ['single_wash' as RewardRuleServiceType, Validators.required],
      selected_package_ids: [[], [Validators.required, minArrayLength(1)]],
      items_sold: [
        1,
        [Validators.required, Validators.min(ITEMS_SOLD_MIN), Validators.max(ITEMS_SOLD_MAX)],
      ],
      theme_color: [DEFAULT_THEME_BACKGROUND, Validators.required],
    });
  }

  private buildPayload(): CreateRewardRuleRequest {
    const value = this.form.value;
    return {
      name: String(value.name).trim(),
      amount: Number(value.amount),
      service_type: value.service_type,
      selected_package_ids: value.selected_package_ids,
      items_sold: Number(value.items_sold),
      theme_color: value.theme_color,
    };
  }

  private loadPackages(): void {
    this.isLoadingPackages = true;
    this.packageService
      .getCarWashPackages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isLoadingPackages = false;
          this.allCarWashPackages = (response.data ?? []).filter(
            pkg => pkg.type === 'car_wash' && pkg.package_status
          );
          this.updatePackageOptions(this.form.get('service_type')?.value);
        },
        error: () => {
          this.isLoadingPackages = false;
          this.allCarWashPackages = [];
          this.packageOptions = [];
        },
      });
  }

  private updatePackageOptions(serviceType: RewardRuleServiceType): void {
    let packages = this.allCarWashPackages;
    if (serviceType === 'membership') {
      packages = packages.filter(
        pkg => Array.isArray(pkg.wash_types) && pkg.wash_types.includes('monthly_membership')
      );
    }

    this.packageOptions = packages.map(pkg => ({
      id: pkg.id,
      name: pkg.package_name,
    }));

    const selectedIds: string[] = this.form.get('selected_package_ids')?.value ?? [];
    const validIds = new Set(this.packageOptions.map(option => option.id));
    const nextSelected = selectedIds.filter(id => validIds.has(id));
    if (nextSelected.length !== selectedIds.length) {
      this.form.get('selected_package_ids')?.setValue(nextSelected);
    }
  }

  private resetModalState(): void {
    this.isSubmitting = false;
    this.isClosing = false;
    this.conflictError = '';
    this.resetFormToDefaults();
  }

  private resetFormToDefaults(): void {
    this.form?.reset({
      name: '',
      amount: '',
      service_type: 'single_wash',
      selected_package_ids: [],
      items_sold: 1,
      theme_color: DEFAULT_THEME_BACKGROUND,
    });
    this.form?.markAsUntouched();
    this.form?.markAsPristine();
  }

  private populateFormFieldsFromRule(rule: RewardRule): void {
    this.form.patchValue({
      name: rule.name,
      amount: rule.amount,
      service_type: rule.service_type,
      selected_package_ids: [...rule.selected_package_ids],
      items_sold: rule.items_sold,
      theme_color: rule.theme_color,
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    const body = error.error;
    if (body?.message) {
      return body.message;
    }
    if (body?.errors && typeof body.errors === 'object') {
      const firstKey = Object.keys(body.errors)[0];
      if (firstKey) {
        return body.errors[firstKey];
      }
    }
    return this.isEditMode
      ? 'Failed to update reward rule. Please try again.'
      : 'Failed to create reward rule. Please try again.';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: Record<string, string> = {
      name: 'Reward name',
      amount: 'Reward amount',
      service_type: 'Service type',
      selected_package_ids: 'Wash type',
      items_sold: 'Items sold',
    };
    return labels[fieldName] ?? fieldName;
  }
}
